import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Grid,
  Card,
  CardContent,
  Paper,
  Snackbar,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  CircularProgress
} from '@mui/material';
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SendIcon from '@mui/icons-material/Send';
import moment from 'moment';

const ReportIssue = () => {
  const [issueType, setIssueType] = useState('');
  const [location, setLocation] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState('medium');
  const [reportedTime, setReportedTime] = useState(moment());
  const [attachments, setAttachments] = useState([]);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  
  // 處理表單驗證
  const validateForm = () => {
    const newErrors = {};
    if (!issueType) newErrors.issueType = '請選擇問題類型';
    if (!location) newErrors.location = '請輸入問題位置';
    if (!description) newErrors.description = '請描述問題詳情';
    if (description && description.length < 10) newErrors.description = '描述至少需要10個字符';
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  
  // 處理文件上傳
  const handleFileUpload = (event) => {
    const files = Array.from(event.target.files);
    setAttachments([...attachments, ...files]);
  };
  
  // 移除已上傳的文件
  const handleRemoveFile = (index) => {
    const newAttachments = [...attachments];
    newAttachments.splice(index, 1);
    setAttachments(newAttachments);
  };
  
  // 處理表單提交
  const handleSubmit = (event) => {
    event.preventDefault();
    
    if (validateForm()) {
      setShowConfirmDialog(true);
    }
  };
  
  // 確認提交
  const confirmSubmit = () => {
    setShowConfirmDialog(false);
    setIsSubmitting(true);
    
    // 模擬API請求
    setTimeout(() => {
      setIsSubmitting(false);
      setShowSuccess(true);
      
      // 重置表單
      setIssueType('');
      setLocation('');
      setDescription('');
      setPriority('medium');
      setReportedTime(moment());
      setAttachments([]);
    }, 1500);
  };
  
  return (
    <Box>
      <Typography variant="h5" gutterBottom>報告問題</Typography>
      
      <Paper sx={{ p: 3, mb: 3 }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.issueType}>
                <InputLabel>問題類型</InputLabel>
                <Select
                  value={issueType}
                  onChange={(e) => setIssueType(e.target.value)}
                  label="問題類型"
                >
                  <MenuItem value="maintenance">設施維修</MenuItem>
                  <MenuItem value="security">安全隱患</MenuItem>
                  <MenuItem value="cleanliness">清潔問題</MenuItem>
                  <MenuItem value="noise">噪音投訴</MenuItem>
                  <MenuItem value="other">其他</MenuItem>
                </Select>
                {errors.issueType && (
                  <Typography variant="caption" color="error">
                    {errors.issueType}
                  </Typography>
                )}
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth error={!!errors.location}>
                <TextField
                  label="問題位置"
                  value={location}
                  onChange={(e) => setLocation(e.target.value)}
                  placeholder="例如: 大堂, 電梯, A座12樓等"
                  error={!!errors.location}
                  helperText={errors.location}
                />
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <FormControl fullWidth>
                <InputLabel>優先級</InputLabel>
                <Select
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  label="優先級"
                >
                  <MenuItem value="low">低 - 不急</MenuItem>
                  <MenuItem value="medium">中 - 普通</MenuItem>
                  <MenuItem value="high">高 - 重要</MenuItem>
                  <MenuItem value="urgent">緊急 - 需立即處理</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DateTimePicker
                  label="報告時間"
                  value={reportedTime}
                  onChange={(newValue) => setReportedTime(newValue)}
                  renderInput={(params) => <TextField {...params} fullWidth />}
                />
              </LocalizationProvider>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="問題描述"
                multiline
                rows={4}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="請詳細描述您遇到的問題..."
                error={!!errors.description}
                helperText={errors.description}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Button
                variant="outlined"
                component="label"
                startIcon={<CloudUploadIcon />}
                sx={{ mb: 2 }}
              >
                上傳相關照片或文件
                <input
                  type="file"
                  hidden
                  multiple
                  onChange={handleFileUpload}
                />
              </Button>
              
              {attachments.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    已上傳文件 ({attachments.length})
                  </Typography>
                  <Grid container spacing={1}>
                    {attachments.map((file, index) => (
                      <Grid item xs={12} sm={6} md={4} key={index}>
                        <Card variant="outlined">
                          <CardContent sx={{ py: 1 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <Typography variant="body2" noWrap sx={{ maxWidth: '70%' }}>
                                {file.name}
                              </Typography>
                              <Button 
                                size="small" 
                                color="error"
                                onClick={() => handleRemoveFile(index)}
                              >
                                移除
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>
                    ))}
                  </Grid>
                </Box>
              )}
            </Grid>
            
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="large"
                  startIcon={<SendIcon />}
                  disabled={isSubmitting}
                >
                  {isSubmitting ? '提交中...' : '提交報告'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
      </Paper>
      
      {/* 確認對話框 */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>確認提交</DialogTitle>
        <DialogContent>
          <DialogContentText>
            您確定要提交此問題報告嗎？提交後將無法修改。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>取消</Button>
          <Button onClick={confirmSubmit} variant="contained">
            確認提交
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* 提交加載中覆蓋層 */}
      {isSubmitting && (
        <Dialog open={isSubmitting}>
          <DialogContent sx={{ textAlign: 'center', py: 4 }}>
            <CircularProgress sx={{ mb: 2 }} />
            <DialogContentText>
              正在提交您的報告，請稍候...
            </DialogContentText>
          </DialogContent>
        </Dialog>
      )}
      
      {/* 成功提示 */}
      <Snackbar 
        open={showSuccess} 
        autoHideDuration={6000} 
        onClose={() => setShowSuccess(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setShowSuccess(false)} 
          severity="success" 
          variant="filled"
          sx={{ width: '100%' }}
        >
          問題報告已成功提交！我們將盡快處理。
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ReportIssue; 